if (!customElements.get('room-size-calculator')) {
  class RoomSizeCalculator extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.js-product');
      this.form = this.section?.querySelector('.js-product-form-main');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.quantityInput = this.form?.querySelector('input[name="quantity"]');
      this.surfaces = this.querySelector('[data-surfaces]');
      this.surfaceTemplate = this.querySelector('[data-surface-template]');
      this.addButton = this.querySelector('[data-add-surface]');
      this.totalAreaInput = this.querySelector('[data-total-area]');
      this.wasteInput = this.querySelector('[data-waste]');
      this.result = this.querySelector('[data-result]');
      this.empty = this.querySelector('[data-empty]');
      this.mode = 'surfaces';
      this.surfaceIndex = 0;
      this.variants = this.parseVariants();
      this.currentVariantId = this.variantInput?.value || this.dataset.currentVariantId;
      this.numberFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        maximumFractionDigits: 2
      });

      this.surfaces.querySelectorAll('[data-surface]').forEach((surface) => {
        this.prepareSurface(surface);
      });
      this.updateRemoveButtons();
      this.addListeners();
      this.calculate();
    }

    disconnectedCallback() {
      this.removeListeners();
    }

    parseVariants() {
      const script = this.querySelector('[data-calculator-variants]');
      if (!script) return [];

      try {
        const variants = JSON.parse(script.textContent);
        return variants.map((variant) => ({
          ...variant,
          coverageSqFt: this.coverageToSquareFeet(variant.coverage, variant.coverageUnit),
          coverage: Number(variant.coverage)
        }));
      } catch (error) {
        console.error('Unable to read room calculator variant data.', error); // eslint-disable-line no-console
        return [];
      }
    }

    addListeners() {
      this.boundHandleInput = this.handleInput.bind(this);
      this.boundHandleMode = this.handleMode.bind(this);
      this.boundAddSurface = this.addSurface.bind(this);
      this.boundRemoveSurface = this.handleRemoveSurface.bind(this);
      this.boundVariantChange = this.handleVariantChange.bind(this);

      this.addEventListener('input', this.boundHandleInput);
      this.addEventListener('change', this.boundHandleInput);
      this.querySelectorAll('[data-area-mode]').forEach((button) => {
        button.addEventListener('click', this.boundHandleMode);
      });
      this.addButton.addEventListener('click', this.boundAddSurface);
      this.surfaces.addEventListener('click', this.boundRemoveSurface);
      document.addEventListener('on:variant:change', this.boundVariantChange);
    }

    removeListeners() {
      this.removeEventListener('input', this.boundHandleInput);
      this.removeEventListener('change', this.boundHandleInput);
      this.querySelectorAll('[data-area-mode]').forEach((button) => {
        button.removeEventListener('click', this.boundHandleMode);
      });
      this.addButton?.removeEventListener('click', this.boundAddSurface);
      this.surfaces?.removeEventListener('click', this.boundRemoveSurface);
      document.removeEventListener('on:variant:change', this.boundVariantChange);
    }

    handleInput(event) {
      if (event.target.matches('[data-width], [data-height], [data-total-area], [data-waste]')) {
        this.calculate();
      }
    }

    handleMode(event) {
      const nextMode = event.currentTarget.dataset.areaMode;
      if (!nextMode || nextMode === this.mode) return;

      this.mode = nextMode;
      this.querySelectorAll('[data-area-mode]').forEach((button) => {
        const isActive = button.dataset.areaMode === this.mode;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      this.querySelector('[data-surfaces-panel]').hidden = this.mode !== 'surfaces';
      this.querySelector('[data-total-panel]').hidden = this.mode !== 'total';
      this.calculate();
    }

    handleVariantChange(event) {
      const product = event.target.closest?.('.js-product');
      if (product !== this.section) return;

      window.requestAnimationFrame(() => {
        this.currentVariantId = this.variantInput?.value || this.currentVariantId;
        this.calculate();
      });
    }

    addSurface() {
      if (!this.surfaceTemplate) return;

      const fragment = this.surfaceTemplate.content.cloneNode(true);
      const surface = fragment.querySelector('[data-surface]');
      this.surfaces.appendChild(fragment);
      this.prepareSurface(surface);
      this.updateRemoveButtons();
      surface.querySelector('[data-width]')?.focus();
    }

    handleRemoveSurface(event) {
      const button = event.target.closest('[data-remove-surface]');
      if (!button) return;

      const surface = button.closest('[data-surface]');
      if (!surface || this.surfaces.querySelectorAll('[data-surface]').length <= 1) return;

      surface.remove();
      this.renumberSurfaces();
      this.updateRemoveButtons();
      this.calculate();
    }

    prepareSurface(surface) {
      this.surfaceIndex += 1;
      const id = `${this.dataset.sectionId || 'calculator'}-surface-${this.surfaceIndex}`;
      const width = surface.querySelector('[data-width]');
      const height = surface.querySelector('[data-height]');

      width.id = `${id}-width`;
      height.id = `${id}-height`;
      const fields = surface.querySelectorAll('.room-size-calculator__field');
      fields[0]?.querySelector('.label')?.setAttribute('for', width.id);
      fields[1]?.querySelector('.label')?.setAttribute('for', height.id);
      surface.querySelector('[data-surface-number]').textContent = this.surfaces.querySelectorAll('[data-surface]').length;
      surface.querySelector('[data-remove-surface]')?.setAttribute(
        'aria-label',
        `Remove wall ${this.surfaces.querySelectorAll('[data-surface]').length}`
      );
    }

    renumberSurfaces() {
      this.surfaces.querySelectorAll('[data-surface]').forEach((surface, index) => {
        surface.querySelector('[data-surface-number]').textContent = index + 1;
        surface.querySelector('[data-remove-surface]')?.setAttribute('aria-label', `Remove wall ${index + 1}`);
      });
    }

    updateRemoveButtons() {
      const surfaces = this.surfaces.querySelectorAll('[data-surface]');
      surfaces.forEach((surface) => {
        const button = surface.querySelector('[data-remove-surface]');
        if (button) button.hidden = surfaces.length === 1;
      });
    }

    getMeasuredArea() {
      if (this.mode === 'total') {
        return this.positiveNumber(this.totalAreaInput?.value);
      }

      return Array.from(this.surfaces.querySelectorAll('[data-surface]')).reduce((total, surface) => {
        return total
          + this.positiveNumber(surface.querySelector('[data-width]')?.value)
          * this.positiveNumber(surface.querySelector('[data-height]')?.value);
      }, 0);
    }

    getCurrentVariant() {
      const variantId = String(this.variantInput?.value || this.currentVariantId || '');
      return this.variants.find((variant) => String(variant.id) === variantId);
    }

    coverageToSquareFeet(quantity, unit) {
      const value = this.positiveNumber(quantity);
      const normalized = String(unit || '').toLowerCase().replace(/\s+/g, '');

      if (!value) return 0;
      if (['ft2', 'sqft', 'ft²', 'squarefeet'].includes(normalized)) return value;
      if (['m2', 'sqm', 'm²', 'squaremetres', 'squaremeters'].includes(normalized)) {
        return value * 10.763910417;
      }

      return 0;
    }

    positiveNumber(value) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) && number > 0 ? number : 0;
    }

    calculate() {
      const measuredArea = this.getMeasuredArea();
      const variant = this.getCurrentVariant();

      if (measuredArea <= 0 || !variant?.coverageSqFt) {
        this.result.hidden = true;
        this.empty.hidden = false;
        this.empty.textContent = variant && measuredArea > 0
          ? 'This box size does not have a coverage measurement yet.'
          : 'Enter at least one wall measurement to calculate a quantity.';
        return;
      }

      const wasteMultiplier = this.wasteInput?.checked ? 1.1 : 1;
      const requiredArea = measuredArea * wasteMultiplier;
      const boxCount = Math.max(1, Math.ceil((requiredArea / variant.coverageSqFt) - 1e-9));
      const purchasedCoverage = boxCount * variant.coverageSqFt;

      this.querySelector('[data-box-result]').textContent = boxCount === 1
        ? '1 box required'
        : `${boxCount} boxes required`;
      this.querySelector('[data-box-note]').textContent = 'Cart quantity updated';
      this.querySelector('[data-measured-area]').textContent = this.formatArea(measuredArea);
      this.querySelector('[data-required-area]').textContent = this.formatArea(requiredArea);
      this.querySelector('[data-box-coverage]').textContent = this.formatArea(variant.coverageSqFt);
      this.querySelector('[data-purchased-coverage]').textContent = this.formatArea(purchasedCoverage);

      this.empty.hidden = true;
      this.result.hidden = false;
      this.setCartQuantity(boxCount);
    }

    setCartQuantity(quantity) {
      const quantityInput = this.form?.querySelector('input[name="quantity"]');
      if (!quantityInput || quantityInput.value === String(quantity)) return;

      quantityInput.value = String(quantity);
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    formatArea(area) {
      return `${this.numberFormatter.format(area)} sq ft`;
    }
  }

  customElements.define('room-size-calculator', RoomSizeCalculator);
}
